import React from 'react'
import classNames from 'classnames'
import { CustomDropdownItemOption, type DropDownItemOptionComponentProps } from '../../../SelectDropdown/SelectDropdown'
import selectDropdownStyles from '../../../SelectDropdown/SelectDropdown.module.scss'
import { RawReferenceFallback } from './RawReferenceFallback'
import { ReferencePicker } from './ReferencePicker'
import { optionLabelOf, useTemplateLibraryOptions } from './referenceOptionSources'
import { type ReferenceValueLabelProps, type ZocksReferenceWidgetProps } from './registry'

const TemplateDropdownItemOption: React.FC<DropDownItemOptionComponentProps> = ({
  optionLabel,
  displayLabel,
  onClick,
  disabled = false,
  deselect = false,
  className = '',
}) => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) {
      return
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onClick(event as unknown as React.MouseEvent<HTMLElement>)
    }
  }

  return (
    <CustomDropdownItemOption
      role='button'
      tabIndex={disabled ? -1 : 0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className={classNames(className, {
        [selectDropdownStyles.selectDropdownCustomItemOptionDisabled]: disabled,
        [selectDropdownStyles.selectDropdownCustomItemOptionIsDeselect]: deselect,
      })}
    >
      {displayLabel ?? optionLabel}
    </CustomDropdownItemOption>
  )
}

interface TemplateLibraryPickerProps extends ZocksReferenceWidgetProps {
  templateType: string
  typeId: string | undefined
}

const TemplateLibraryPicker: React.FC<TemplateLibraryPickerProps> = ({ templateType, typeId, ...props }) => {
  const { options, loading } = useTemplateLibraryOptions(templateType, typeId, props.layer)
  return <ReferencePicker {...props} options={options} loading={loading} dropDownItemOptionComponent={TemplateDropdownItemOption} />
}

export const TemplateLibraryWidget: React.FC<ZocksReferenceWidgetProps> = (props) => {
  const templateType = props.config.templateType
  const typeId = typeof props.config.typeId === 'string' ? props.config.typeId : undefined

  if (typeof templateType !== 'string' || !templateType) {
    return <RawReferenceFallback {...props} />
  }

  return <TemplateLibraryPicker {...props} templateType={templateType} typeId={typeId} />
}

const TemplateName: React.FC<ReferenceValueLabelProps & { templateType: string; typeId: string | undefined }> = ({
  templateType,
  typeId,
  layer,
  value,
  fallback,
}) => <>{optionLabelOf(useTemplateLibraryOptions(templateType, typeId, layer), value, fallback)}</>

export const TemplateLibraryValueLabel: React.FC<ReferenceValueLabelProps> = (props) => {
  const templateType = props.config.templateType
  const typeId = typeof props.config.typeId === 'string' ? props.config.typeId : undefined
  return typeof templateType === 'string' && templateType ? <TemplateName {...props} templateType={templateType} typeId={typeId} /> : <>{props.fallback}</>
}
